import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { setAuthToken, markAuthResolved, queryClient } from "@/lib/query-client";
import { mintDemoToken } from "@/lib/demo-token";
import { usePushToken, unregisterPushToken } from "./usePushToken";

type FirebaseUserLike = {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
    getIdToken: () => Promise<string>;
};

import { loadFirebase } from "@/lib/firebaseLoader";

export interface User {
    id: string;
    email: string;
    name: string;
    picture?: string;
    isDemo?: boolean;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    enterDemo: (user: User) => Promise<void>;
    signOut: () => Promise<void>;
    /**
     * Resolve the real session before deciding someone is a guest. On web the
     * Firebase restore waits for the first tap, so a returning subscriber who
     * taps "Subscribe" first is still on the demo session at that moment.
     * Starts the restore if needed; resolves the Firebase user, or null when
     * there is none (or it did not answer within the timeout).
     */
    ensureSession: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_USER_KEY = "@fahali_auth_user";

/*
  The single Firebase-user -> app-user mapping. It was private, so AuthScreen
  passed the raw Firebase user straight into signIn(): Firebase exposes `uid`
  and `displayName`, the app expects `id` and `name`, so the signed-in user
  briefly had `id: undefined` on the email login path until the auth-state
  listener happened to overwrite it. Exported so there is one mapping, not two.
*/
export function firebaseUserToUser(fbUser: FirebaseUserLike): User {
    return {
        id: fbUser.uid,
        email: fbUser.email || "",
        name: fbUser.displayName || "Fahali User",
        picture: fbUser.photoURL || undefined,
    };
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const startRestoreRef = useRef<() => void>(() => {});
    const firstRestoreRef = useRef<Promise<User | null> | null>(null);

    useEffect(() => {
        // The public reader is usable without an account. Start its short-lived
        // demo session immediately, then restore Firebase in the background.
        // Firebase's hidden auth iframe is expensive on a cold mobile network;
        // it must never decide when Today first becomes readable.
        let disposed = false;
        let revision = 0;
        let unsubscribe: (() => void) | undefined;
        let settleFirst: (u: User | null) => void = () => {};
        firstRestoreRef.current = new Promise<User | null>((resolve) => { settleFirst = resolve; });

        void bootAsGuest();

        const beginFirebaseRestore = () => {
          void loadFirebase().then(({ onIdTokenChange }) => {
            if (disposed) return;
            // Firebase silently rotates ID tokens during a long-lived session.
            // Listen to token changes, not only sign-in/out, so API requests
            // never keep using an expired bearer captured at initial login.
            unsubscribe = onIdTokenChange((fbUser) => {
              if (disposed) return;
            const observedRevision = ++revision;
            if (fbUser) {
                const appUser = firebaseUserToUser(fbUser);
                AsyncStorage.setItem(AUTH_USER_KEY, JSON.stringify(appUser)).catch(() => {});
                // Mirror the current Firebase ID token for every API request.
                // The callback also runs on token rotation, so this replaces an
                // expired token without asking the user to refresh or sign in.
                fbUser.getIdToken().then((token) => {
                    if (observedRevision !== revision) return;
                    setAuthToken(token);
                    setUser(appUser);
                    setIsLoading(false);
                    settleFirst(appUser);
                }).catch(() => {
                    if (observedRevision !== revision) return;
                    setAuthToken(null);
                    markAuthResolved();
                    setUser(null);
                    setIsLoading(false);
                    settleFirst(null);
                });
            } else {
                void loadStoredUser();
                settleFirst(null);
            }
            });
          }).catch(() => {
            // The demo session and reader remain available when an auth service
            // is unreachable. A sign-in attempt will surface its own error.
            settleFirst(null);
          });
        };
        // On web, wait until the reader engages. A background auth iframe is
        // not market intelligence and was repeatedly becoming the first
        // reader's largest task. AuthScreen still loads Firebase immediately
        // when a person explicitly asks to sign in. Native keeps its short
        // background restore because it has no browser interaction boundary.
        let firebaseStarted = false;
        const startOnInteraction = () => {
          if (firebaseStarted) return;
          firebaseStarted = true;
          beginFirebaseRestore();
        };
        startRestoreRef.current = startOnInteraction;
        const schedule = Platform.OS === "web" ? null : setTimeout(startOnInteraction, 5000);
        if (Platform.OS === "web" && typeof window !== "undefined") {
          window.addEventListener("pointerdown", startOnInteraction, { once: true, passive: true });
          window.addEventListener("keydown", startOnInteraction, { once: true });
        }

        return () => {
            disposed = true;
            revision += 1;
            unsubscribe?.();
            if (schedule) clearTimeout(schedule);
            if (Platform.OS === "web" && typeof window !== "undefined") {
              window.removeEventListener("pointerdown", startOnInteraction);
              window.removeEventListener("keydown", startOnInteraction);
            }
        };
    }, []);

    // Signed-out boot: mint a server demo token for the "look around first"
    // flow, then load the stored (demo) user and unblock the query boot gate.
    const bootAsGuest = async () => {
        try {
            await mintDemoToken();
        } finally {
            markAuthResolved();
            await loadStoredUser();
        }
    };

    const loadStoredUser = async () => {
        try {
            const storedUser = await AsyncStorage.getItem(AUTH_USER_KEY);
            if (storedUser) {
                const stored = JSON.parse(storedUser);
                // Cached identity is not authentication. Only the explicit
                // sample user can be restored without Firebase.
                setUser(stored?.isDemo === true ? stored : null);
            } else {
                setUser(null);
            }
        } catch (e) {
            /* production: no log */
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Demo is deliberately separate from Firebase authentication. A generic
    // signIn setter let the email path overwrite a real Firebase user with
    // isDemo=true depending on observer timing.
    const enterDemo = async (newUser: User) => {
        try {
            await AsyncStorage.setItem(AUTH_USER_KEY, JSON.stringify({ ...newUser, isDemo: true }));
            setUser({ ...newUser, isDemo: true });
        } catch (e) {
            /* production: no log */
        }
    };

    const signOut = async () => {
        await unregisterPushToken().catch(() => {});
        const { signOutFirebase } = await loadFirebase();
        await signOutFirebase();
        setAuthToken(null);
        await AsyncStorage.removeItem(AUTH_USER_KEY);
        queryClient.clear();
        setUser(null);
    };

    const ensureSession = async (): Promise<User | null> => {
        if (user && !user.isDemo) return user;
        startRestoreRef.current();
        const first = firstRestoreRef.current;
        if (!first) return null;
        return Promise.race([
            first,
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 8000)),
        ]);
    };

    usePushToken(user?.isDemo ? null : user?.id);

    return (
        <AuthContext.Provider value={{ user, isLoading, enterDemo, signOut, ensureSession }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
